import { centsToDollars, parsePrice } from "helpers/price";
import {
  IOrderBalance,
  IOrderBalanceBox,
  IOrderBalanceBoxRow,
  IOrderBalanceEvent,
  IOrderBalanceInvariant,
  IOrderBalanceParty,
} from "types/OrderBalance";

export const formatCents = (cents: number | null | undefined): string =>
  cents === null || cents === undefined
    ? ""
    : parsePrice(centsToDollars(cents));

export const formatDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString("en-US", { timeZone: "UTC" }) : "";

export type EventTableRow = Record<string, string | number | boolean | null>;

export const partyField = (
  partyId: string,
  field: "total" | "provider" | "fee"
): string => `party_${partyId}_${field}`;

/**
 * Flattens ledger events into DataTable rows: one row per event, three columns per provider
 * (Total / Provider / Clear Fee), exactly like the workbook's Order Overview and Payment Activity.
 */
export const buildEventRows = (
  events: IOrderBalanceEvent[],
  providers: IOrderBalanceParty[]
): EventTableRow[] =>
  events.map((event) => {
    const parties = Object.values(event.byParty);
    const row: EventTableRow = {
      id: event.id,
      date: event.date,
      type: event.type,
      label: event.label,
      patient: event.patientInCents,
      // The workbook's "Provider Split Total" and "Clear Total" (every Clear fee plus Clear's own cash).
      providerTotal: parties.reduce((sum, a) => sum + a.providerInCents, 0),
      clearTotal:
        parties.reduce((sum, a) => sum + a.clearFeeInCents, 0) +
        event.clearDirectInCents,
      clearDirect: event.clearDirectInCents,
      unallocated: event.unallocatedInCents,
      verified: event.verified,
      notes: event.notes.join(" "),
      informational: !!event.informational,
    };
    providers.forEach((provider) => {
      const amounts = event.byParty[provider.id];
      row[partyField(provider.id, "total")] = amounts?.totalInCents ?? null;
      row[partyField(provider.id, "provider")] =
        amounts?.providerInCents ?? null;
      row[partyField(provider.id, "fee")] = amounts?.clearFeeInCents ?? null;
    });
    return row;
  });

/**
 * PASS / FAIL checks are listed; SKIPPED ones (not applicable to this order) are tucked behind one
 * muted line. The JSON still carries all of them.
 */
export const partitionChecks = (
  invariants: IOrderBalanceInvariant[]
): {
  applicable: IOrderBalanceInvariant[];
  notApplicable: IOrderBalanceInvariant[];
} => ({
  applicable: invariants.filter((check) => check.status !== "SKIPPED"),
  notApplicable: invariants.filter((check) => check.status === "SKIPPED"),
});

// Informational rows (the estimate, balance adjustments) are shown for reference and never added into a total.
export const sumField = (rows: EventTableRow[], field: string): number =>
  rows.reduce(
    (sum, row) => (row.informational ? sum : sum + (Number(row[field]) || 0)),
    0
  );

/**
 * The one-line verdict. Uncollected money is Outstanding, not a balance: an order whose collected
 * money is all where it should be reads "In balance" however much the patient still owes.
 */
export const balanceVerdict = (
  ledger: IOrderBalance
): { ok: boolean; text: string } => {
  const failing = ledger.invariants.filter((i) => i.status === "FAIL").length;
  const box = ledger.balanceBox;
  if (!box.allZero || failing > 0) {
    const checks = `${failing} check${failing === 1 ? "" : "s"} failing.`;
    // Only the reported side is off: the cash is where it should be, the weekly reports lag behind.
    if (!box.allZero && box.cashBalanced) {
      return {
        ok: false,
        text: `Cash balanced; reported figures trail — ${checks}`,
      };
    }
    return { ok: false, text: `Out of balance — ${checks}` };
  }
  // Rows in PLINE reports: those reported figures are never comparable (the report does not name the order).
  const pline = (box.reportedNotComparablePartyIds ?? []).length > 0;
  let pending = box.reportsCoverOrder
    ? ""
    : " Reported balances pending (no weekly report yet).";
  if (pline) pending = " Some reported figures not comparable (PLINE reports).";
  if (box.collectedInCents === 0) {
    return {
      ok: true,
      text: `Nothing collected yet${
        box.patientBalanceInCents > 0
          ? ` — ${formatCents(box.patientBalanceInCents)} to be collected`
          : ""
      }.`,
    };
  }
  if (box.patientBalanceInCents > 0) {
    return {
      ok: true,
      text: `In balance — ${formatCents(
        box.patientBalanceInCents
      )} still to be collected.${pending}`,
    };
  }
  if (pline) {
    return {
      ok: true,
      text: "In balance: every cash balance is zero; some reported figures are in PLINE reports (not comparable).",
    };
  }
  return {
    ok: true,
    text: box.reportsCoverOrder
      ? "In balance: every balance is zero."
      : "In balance: every cash balance is zero; reported balances pending (no weekly report yet).",
  };
};

/** "Clear fee 3% · patient fee 3%": the patient fee is named whenever the order has one. */
export const feeRatesText = (ledger: IOrderBalance): string => {
  const clearFee =
    ledger.clearFeeRatePercent === null
      ? "Clear fee n/a"
      : `Clear fee ${ledger.clearFeeRatePercent}%`;
  return ledger.clearPatientFeeRatePercent
    ? `${clearFee} · patient fee ${ledger.clearPatientFeeRatePercent}%`
    : clearFee;
};

/**
 * The balance box headings, exactly as finance's audit template names them (the last two are the
 * panel's own), each with a one-sentence explanation for support staff who have not seen the workbook.
 * Labels only: the JSON field names stay as they are.
 */
export const BALANCE_BOX_HEADINGS: Partial<
  Record<keyof IOrderBalanceBoxRow, { header: string; tooltip: string }>
> = {
  providerOwedInCents: {
    header: "Provider Owed (net)",
    tooltip:
      "What this party should have received, after Clear's fee, from the payments collected so far and the transfers staff asked for.",
  },
  providerPaidInCents: {
    header: "Provider Paid",
    tooltip:
      "What this party actually received at Stripe / Finix, from our processor records.",
  },
  reportedPaidInCents: {
    header: "Reported Paid",
    tooltip:
      "What the weekly reports told this party it was paid (Amount Paid); for Clear Health, the Clear Fee Withheld.",
  },
  providerTotalInCents: {
    header: "Provider TPP",
    tooltip:
      "Total Patient Payment this party should show: its owed share plus Clear's fee on it, without the Clear patient fee.",
  },
  reportedTotalInCents: {
    header: "Reported Provider TPP",
    tooltip: "Total Patient Payment the weekly reports showed for this party.",
  },
  cashBalanceInCents: {
    header: "Cash Balance",
    tooltip:
      "Provider Owed (net) minus Provider Paid: money still to move at the processor. Should be $0.00.",
  },
  reportedCashBalanceInCents: {
    header: "Reported Cash Balance",
    tooltip:
      "Provider Owed (net) minus Reported Paid: what the reports still have to show as paid. Should be $0.00.",
  },
  reportedTppBalanceInCents: {
    header: "Reported TPP Balance",
    tooltip:
      "Provider TPP minus Reported Provider TPP: patient payment the reports have not attributed. Should be $0.00.",
  },
  outstandingInCents: {
    header: "Outstanding",
    tooltip:
      "The estimate (after any balance adjustments) not collected from the patient yet. Not a balance.",
  },
  clearPatientFeeInCents: {
    header: "Clear Patient Fee",
    tooltip:
      "Collected by Clear on top of the estimate; weekly reports never show it, so it is left out of Provider TPP.",
  },
};

/** One-sentence explanations for the Order Overview / Payment Processor Activity headings. */
export const EVENT_TABLE_TOOLTIPS = {
  date: "When the payment, transfer or balance change was recorded (UTC date).",
  type: "What the row is: the estimate, a balance adjustment, a payment, a refund or a transfer between parties.",
  orderTotal:
    "What the patient pays in this row; the estimate and balance adjustments are shown for reference and left out of the totals.",
  patient:
    "Patient cash that moved at the processor in this row: a payment in, or a refund out.",
  providerSplitTotal:
    "Every provider's share of this row after Clear's fee, added together.",
  clearTotal:
    "Every Clear fee on the row plus cash Clear Health moved directly.",
  total:
    "This provider's part of the patient payment: its Provider Split plus the Clear Fee on it.",
  provider: "What this provider keeps from the row, after Clear's fee.",
  fee: "Clear's fee on this provider's part of the row, including any Clear patient fee.",
};

export const REPORTED_FIELDS: (keyof IOrderBalanceBoxRow)[] = [
  "reportedPaidInCents",
  "reportedTotalInCents",
  "reportedCashBalanceInCents",
  "reportedTppBalanceInCents",
];

// Clear Health has no Total / TPP columns in the workbook: those cells stay blank, never "pending".
const CLEAR_BLANK_FIELDS: (keyof IOrderBalanceBoxRow)[] = [
  "reportedTotalInCents",
  "reportedTppBalanceInCents",
];

/**
 * True when a balance-box cell is a reported figure of a party whose rows are in PLINE reports — a
 * PLINE report never names the order, so the figure is null and renders as a muted "PLINE" marker.
 */
export const isReportedNotComparable = (
  balanceBox: IOrderBalanceBox,
  row: IOrderBalanceBoxRow,
  field: keyof IOrderBalanceBoxRow
): boolean =>
  REPORTED_FIELDS.includes(field) &&
  row[field] === null &&
  !(row.kind === "CLEAR_HEALTH" && CLEAR_BLANK_FIELDS.includes(field)) &&
  ((balanceBox.reportedNotComparablePartyIds ?? []).includes(row.partyId) ||
    (row.kind === "TOTAL" &&
      (balanceBox.reportedNotComparablePartyIds ?? []).length > 0));

/**
 * True when a balance-box cell is a reported figure that no weekly report covers yet — rendered as a
 * muted "pending" marker rather than $0.00, which would read as a discrepancy.
 */
export const isReportedPending = (
  balanceBox: IOrderBalanceBox,
  row: IOrderBalanceBoxRow,
  field: keyof IOrderBalanceBoxRow
): boolean =>
  !balanceBox.reportsCoverOrder &&
  !isReportedNotComparable(balanceBox, row, field) &&
  REPORTED_FIELDS.includes(field) &&
  row[field] === null &&
  !(row.kind === "CLEAR_HEALTH" && CLEAR_BLANK_FIELDS.includes(field));

// The table renders "<type> — <label>"; a label that already names the type is shown on its own.
export const eventTypeText = (type: string, label: string): string => {
  if (!label || label === type) return type;
  return label.startsWith(`${type} — `) ? label : `${type} — ${label}`;
};
